// src/components/Layout.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Menu } from 'lucide-react';

// ─── Brand tokens ──────────────────────────────────────────────────────────────
const C = {
  navy:      '#001f3f',
  navyLight: '#002d5a',
  teal:      '#0d9488',
  tealLight: '#14b8a6',
  tealGlow:  'rgba(13,148,136,0.15)',
  white:     '#ffffff',
  slate200:  '#e2e8f0',
  slate300:  '#cbd5e1',
  slate400:  '#94a3b8',
  slate500:  '#64748b',
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const isPathActive = (itemPath, currentPath, currentSearch) => {
  if (!itemPath) return false;
  const [base, qs] = itemPath.split('?');
  if (qs) {
    const params        = new URLSearchParams(qs);
    const currentParams = new URLSearchParams(currentSearch);
    if (base !== currentPath) return false;
    for (const [k, v] of params.entries()) {
      if (currentParams.get(k) !== v) return false;
    }
    return true;
  }
  return currentPath === base;
};

const hasActiveDescendant = (item, pathname, search) => {
  if (isPathActive(item.path, pathname, search)) return true;
  return item.children?.some(c => hasActiveDescendant(c, pathname, search)) || false;
};

// ── Logo ───────────────────────────────────────────────────────────────────────
const LogoMark = () => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '18px 16px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    flexShrink: 0,
  }}>
    <div style={{
      width: 36, height: 36, borderRadius: 9, flexShrink: 0,
      background: `linear-gradient(135deg, ${C.teal}, ${C.navyLight})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 12px rgba(13,148,136,0.3)',
    }}>
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <path d="M10 2L3 5v5c0 4.418 3.134 8.56 7 9.5C13.866 18.56 17 14.418 17 10V5L10 2z"
          fill="white" fillOpacity="0.9" />
        <path d="M7 10l2 2 4-4" stroke={C.teal} strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
    <div style={{ minWidth: 0 }}>
      <p style={{ color: C.white, fontWeight: 700, fontSize: 14, lineHeight: 1.2, margin: 0 }}>
        JamboRicG
      </p>
      <p style={{ color: C.tealLight, fontSize: 10.5, margin: '2px 0 0', opacity: 0.85 }}>
        Admin Portal
      </p>
    </div>
  </div>
);

// ── Menu item ──────────────────────────────────────────────────────────────────
const MenuItem = ({ item, depth = 0, openMenus, toggleMenu, onNavigate, pathname, search }) => {
  const hasChildren    = Boolean(item.children?.length);
  const isOpen         = openMenus[item.label] || false;
  const isActive       = isPathActive(item.path, pathname, search);
  const isParentActive = hasChildren && hasActiveDescendant(item, pathname, search);

  // ── Key fix: cap indentation so deep items never run out of space ──────────
  // depth 0 → 12px left pad | depth 1 → 22px | depth 2 → 30px | depth 3+ → 34px
  const PAD = [12, 22, 30, 34];
  const paddingLeft = PAD[Math.min(depth, PAD.length - 1)];

  // Slightly smaller font / row height at deeper levels
  const fontSize   = depth === 0 ? 13.5 : depth === 1 ? 13 : 12.5;
  const rowPadY    = depth === 0 ? 10 : 8;
  const iconSize   = depth <= 1 ? 18 : 16;

  const bg = isActive ? C.teal : 'transparent';

  const labelColor = isActive
    ? C.white
    : isParentActive
      ? C.tealLight
      : depth === 0
        ? C.slate200
        : C.slate300;

  const handleClick = (e) => {
    e.stopPropagation();
    if (hasChildren)   { toggleMenu(item.label); return; }
    if (item.onClick)  { item.onClick(); return; }
    if (item.path)     { onNavigate(item.path); }
  };

  return (
    <div>
      {/* Row */}
      <div
        onClick={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          paddingLeft,
          paddingRight: 10,
          paddingTop: rowPadY,
          paddingBottom: rowPadY,
          margin: '1px 6px',
          borderRadius: 9,
          cursor: 'pointer',
          backgroundColor: bg,
          position: 'relative',
          transition: 'background-color 0.13s ease',
          // ── Critical: no overflow hidden on the row itself ──
          overflow: 'visible',
        }}
        onMouseEnter={e => {
          if (!isActive)
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)';
        }}
        onMouseLeave={e => {
          if (!isActive)
            e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {/* Active bar */}
        {isActive && (
          <div style={{
            position: 'absolute', left: -6, top: '50%',
            transform: 'translateY(-50%)',
            width: 3, height: '55%',
            backgroundColor: C.white,
            borderRadius: 2,
          }} />
        )}

        {/* Icon — fixed width so label always starts at the same X */}
        <span style={{
          flexShrink: 0,
          width: iconSize,
          height: iconSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isActive ? C.white : isParentActive ? C.tealLight : C.slate400,
          opacity: isActive ? 1 : 0.8,
        }}>
          {item.icon
            ? (typeof item.icon === 'string' ? item.icon : item.icon)
            : null}
        </span>

        {/* Label — THIS is what was getting clipped before ─────────────────── */}
        <span style={{
          flex: 1,
          fontSize,
          fontWeight: isActive ? 600 : isParentActive ? 500 : 400,
          color: labelColor,
          letterSpacing: '-0.01em',
          lineHeight: 1.35,
          // Use word-break instead of nowrap so long labels wrap rather than vanish
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          minWidth: 0,
        }}>
          {item.label}
        </span>

        {/* Chevron */}
        {hasChildren && (
          <span style={{
            flexShrink: 0,
            color: isParentActive ? C.tealLight : C.slate500,
            display: 'flex',
            alignItems: 'center',
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}>
            <ChevronDown size={13} />
          </span>
        )}
      </div>

      {/* Children — animated */}
      {hasChildren && (
        <div style={{
          overflow: 'hidden',
          maxHeight: isOpen ? 2000 : 0,
          transition: 'max-height 0.22s ease',
        }}>
          {/* Subtle indent guide line */}
          <div style={{
            marginLeft: paddingLeft + 12,
            borderLeft: '1px solid rgba(255,255,255,0.07)',
            paddingTop: 2,
            paddingBottom: 2,
          }}>
            {item.children.map(child => (
              <MenuItem
                key={child.label}
                item={child}
                depth={depth + 1}
                openMenus={openMenus}
                toggleMenu={toggleMenu}
                onNavigate={onNavigate}
                pathname={pathname}
                search={search}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Layout ─────────────────────────────────────────────────────────────────────
const Layout = ({ sidebarItems = [], children }) => {
  const navigate     = useNavigate();
  const location     = useLocation();
  const [openMenus, setOpenMenus]   = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const initialised  = useRef(false);

  // Auto-open parents of the active route
  useEffect(() => {
    const toOpen = {};
    const walk = (items) => {
      items.forEach(item => {
        if (hasActiveDescendant(item, location.pathname, location.search)) {
          toOpen[item.label] = true;
        }
        if (item.children) walk(item.children);
      });
    };
    walk(sidebarItems);
    setOpenMenus(prev => ({ ...prev, ...toOpen }));
    if (initialised.current) setMobileOpen(false);
    initialised.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const toggleMenu    = (label) => setOpenMenus(prev => ({ ...prev, [label]: !prev[label] }));
  const handleNavigate = (path) => { navigate(path); setMobileOpen(false); };

  // ── Sidebar inner content ──────────────────────────────────────────────────
  const SidebarInner = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <LogoMark />

      {/* Scrollable nav */}
      <nav style={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden',
        padding: '10px 0 16px',
        scrollbarWidth: 'thin',
        scrollbarColor: `${C.navyLight} transparent`,
      }}>
        {sidebarItems.map(item => (
          <MenuItem
            key={item.label}
            item={item}
            depth={0}
            openMenus={openMenus}
            toggleMenu={toggleMenu}
            onNavigate={handleNavigate}
            pathname={location.pathname}
            search={location.search}
          />
        ))}
      </nav>

      {/* Footer */}
      <div style={{
        borderTop: '1px solid rgba(255,255,255,0.07)',
        padding: '10px 14px',
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
      }}>
        <div style={{
          width: 7, height: 7, borderRadius: '50%',
          backgroundColor: C.teal,
          boxShadow: `0 0 6px ${C.teal}`,
          flexShrink: 0,
        }} />
        <span style={{ fontSize: 10.5, color: C.slate500 }}>JamboRicG Platform v2</span>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f1f5f9' }}>

      {/* Desktop sidebar — 256px wide gives comfortable room for nested items */}
      <aside
        style={{
          width: 256,
          flexShrink: 0,
          backgroundColor: C.navy,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '3px 0 20px rgba(0,0,0,0.22)',
          position: 'relative',
          zIndex: 10,
        }}
        className="hidden md:flex"
      >
        <SidebarInner />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 40,
            backgroundColor: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(2px)',
          }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <aside
        style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          width: 260, zIndex: 50,
          backgroundColor: C.navy,
          display: 'flex', flexDirection: 'column',
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.22s ease',
          boxShadow: '4px 0 28px rgba(0,0,0,0.35)',
        }}
        className="md:hidden"
      >
        <SidebarInner />
      </aside>

      {/* Main content area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* Mobile top bar */}
        <div
          style={{
            backgroundColor: C.navy, padding: '12px 16px',
            display: 'flex', alignItems: 'center', gap: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)', flexShrink: 0,
          }}
          className="md:hidden flex"
        >
          <button
            onClick={() => setMobileOpen(true)}
            style={{
              color: C.white, background: 'none', border: 'none',
              cursor: 'pointer', padding: 4, display: 'flex',
            }}
          >
            <Menu size={22} />
          </button>
          <span style={{ color: C.white, fontWeight: 700, fontSize: 15 }}>JamboRicG</span>
        </div>

        {/* Page */}
        <main style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f1f5f9' }}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;